import {
  FuelSessionStatus,
  GasStationRoute,
  Location,
  Station,
} from '../@types';
import { gasStations } from '../data';
import { nearestGasStation } from './mapUtils';
import { formatDistance } from './numbersFormatters';
import { formatDistanceFromKilometers } from './distanceFormatters';
import { distanceBetweenLocationsInKm } from './distanceBetweenLocationsInKm';
import { locationFromString } from './locationFromString';

export const DIGITAL_FUEL_STATION_RADIUS_M = 50;
export const DIGITAL_FUEL_STATION_MATCH_RADIUS_M = 50;
export const DIGITAL_FUEL_ACQUIRED_BOX_MS = 120000;

export const DIGITAL_FUEL_SETTINGS_FALLBACKS = {
  title: 'Refuel with your phone',
  desc: 'New: refuel without a fuel card and without cash. The app pays automatically – in three simple steps.',
  stepOne: '1. Find a petrol station',
  stepOneDesc:
    'Tap “Map”. We show you the nearest petrol station where you can pay by app, and the way there.',
  stepTwo: '2. Park at the pump',
  stepTwoDesc:
    'Drive up to an available pump and switch off the engine. Fuelling can only be started when you and the vehicle are right at the station.',
  stepThree: '3. Start fuelling and pay',
  stepThreeDesc:
    'On the station card tap “Start fuelling”, choose the number of your pump and fill up as usual. Afterwards the app pays automatically – no need to go to the counter.',
  faq: '<h4 style="font-weight: 600;">FAQ</h4> <hr style="border: 0.5px solid #0000001f;"> <details style="font-family: AvenirNext-Medium; margin-top: 10px; margin-bottom: 10px;"> <summary> Do I need a fuel card or cash? </summary> <p style="margin-top: 20px;font-family: AvenirNext-Regular;"> No. Payment is handled entirely by the app. You neither insert a card nor go to the counter. </p> </details> <hr style="border: 0.5px solid #0000001f;"> <details style="font-family: AvenirNext-Medium; margin-top: 10px; margin-bottom: 10px;"> <summary> How do I know which petrol station I can use? </summary> <p style="margin-top: 20px;font-family: AvenirNext-Regular;"> Tap “Map”. Only petrol stations where paying by app works are shown, and the nearest one is selected for you. </p> </details> <hr style="border: 0.5px solid #0000001f;"> <details style="font-family: AvenirNext-Medium; margin-top: 10px; margin-bottom: 10px;"> <summary> “Start fuelling” is missing or not working – what now? </summary> <p style="margin-top: 20px;font-family: AvenirNext-Regular;"> Park the vehicle right at the petrol station and allow the app to use your location. Both are needed before fuelling can start. </p> </details> <hr style="border: 0.5px solid #0000001f;"> <details style="font-family: AvenirNext-Medium; margin-top: 10px; margin-bottom: 10px;"> <summary> How do I know everything worked? </summary> <p style="margin-top: 20px;font-family: AvenirNext-Regular;"> After fuelling the app shows a green note “Fuelling completed”. You can drive away then. If something goes wrong, our support is one tap away via the chat icon at the top right. </p> </details> <hr style="border: 0.5px solid #0000001f;">',
};

export const DIGITAL_FUEL_CHOICE_FALLBACKS = {
  title: 'Was möchtest du tanken?',
  fuelTitle: 'Kraftstoff',
  fuelDesc:
    'Per App an der Zapfsäule bezahlen – ohne Tankkarte und ohne Bargeld.',
  adblueTitle: 'AdBlue',
  adblueDesc: 'Mit der Tankkarte aus dem Fahrzeug bezahlen.',
  stationAdblueNote:
    'AdBlue bezahlst du mit der Tankkarte aus dem Fahrzeug – die Bezahlung per App ist für AdBlue noch nicht möglich.',
};

export const isAtDigitalFuelStation = (
  location: Location | null | undefined,
  station: { location?: string | null } | null | undefined
): boolean => {
  const stationLocation = station?.location
    ? locationFromString(station.location)
    : null;
  if (!location || !stationLocation) return false;
  return (
    distanceBetweenLocationsInKm(location, stationLocation) * 1000 <
    DIGITAL_FUEL_STATION_RADIUS_M
  );
};

const RYD_BRAND_PATTERNS: Array<[RegExp, string]> = [
  [/\baral\b/i, 'ARAL'],
  [/\btotal/i, 'TotalEnergies'],
  [/\b(eni|agip)\b/i, 'Eni'],
  [/\bavanti\b/i, 'Avanti'],
  [/\bwestfalen\b/i, 'Westfalen'],
  [/\bomv\b/i, 'OMV'],
  [/\bbp\b/i, 'BP'],
];

export const digitalFuelBrandFromName = (
  name: string | null | undefined
): string | null => {
  if (!name) return null;
  const match = RYD_BRAND_PATTERNS.find(([pattern]) => pattern.test(name));
  return match ? match[1] : null;
};

export const formatDigitalFuelDistance = (
  meters: number | null | undefined
): string | null => {
  if (typeof meters !== 'number' || Number.isNaN(meters)) return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return formatDistance(meters / 1000);
};

export const rentalGasStationDistance = (
  station: any,
  gasStationRoute: GasStationRoute | null | undefined
): string | null => {
  if (!station?.isDigitalFuel) return null;
  return gasStationRoute?.targetId === station.id
    ? formatDistanceFromKilometers(gasStationRoute.distance)
    : formatDigitalFuelDistance(station.distance);
};

export const digitalFuelAvailabilityLabel = (
  localize: any,
  status: string | null | undefined
): string | null => {
  if (!status) return null;
  return status.toUpperCase() === 'ONLINE'
    ? localize('opennow', { fallback: 'Jetzt geöffnet' })
    : localize('digitalFuel.station.offline', {
        fallback: 'Derzeit nicht verfügbar',
      });
};

export const remainingTankCapacityLabel = (
  localize: any,
  remainingTankCapacity: number | null | undefined
): string | null => {
  if (typeof remainingTankCapacity !== 'number') return null;
  return localize(
    'digitalFuel.station.remainingTankCapacity',
    String(remainingTankCapacity),
    { fallback: 'Verbleibende Tankkapazität: %s L' }
  );
};

export const tankCapacityLabel = (
  localize: any,
  tankCapacity: number | null | undefined
): string | null => {
  if (typeof tankCapacity !== 'number' || tankCapacity <= 0) return null;
  return localize('digitalFuel.station.tankCapacity', String(tankCapacity), {
    fallback: 'Tankgröße: %s L',
  });
};

export const fuelLevelLabel = (
  localize: any,
  percentage: number | null | undefined
): string | null => {
  if (typeof percentage !== 'number') return null;
  return localize('digitalFuel.station.currentLevel', String(percentage), {
    fallback: 'Aktueller Füllstand: %s%',
  });
};

const RYD_SUPPORTED_LANGS = [
  'de',
  'en',
  'nl',
  'fr',
  'it',
  'es',
  'pt',
  'da',
  'pl',
] as const;

export const toRydLang = (lang: string | null | undefined): string => {
  const normalized = (lang === 'deformal' ? 'de' : lang || 'de').toLowerCase();
  return (RYD_SUPPORTED_LANGS as readonly string[]).includes(normalized)
    ? normalized
    : 'de';
};

export const isDigitalFuelOffered = (rentalData: any): boolean =>
  !!rentalData?.digitalFuelEnabled;

export const isDigitalFuelAvailable = (rentalData: any): boolean =>
  isDigitalFuelOffered(rentalData) && !rentalData?.refuelBlocked;

export const digitalFuelStationLocation = (
  station: Station | null | undefined
): Location | null =>
  typeof station?.latitude === 'number' &&
  typeof station?.longitude === 'number'
    ? { latitude: station.latitude, longitude: station.longitude }
    : null;

export const digitalFuelStreetAddress = (
  address: string | null | undefined
): string => (address || '').split(',')[0].trim();

export const matchingStoredGasStation = (
  location: Location | null | undefined,
  storedStations: Array<any> = gasStations
): any | null => {
  if (!location || !storedStations?.length) return null;
  const nearest = nearestGasStation(location, storedStations);
  const nearestLocation = nearest?.location
    ? locationFromString(nearest.location)
    : null;
  if (!nearestLocation) return null;
  return distanceBetweenLocationsInKm(location, nearestLocation) * 1000 <=
    DIGITAL_FUEL_STATION_MATCH_RADIUS_M
    ? nearest
    : null;
};

export const toMapGasStation = (station: Station, storedStations?: any) => {
  const location = digitalFuelStationLocation(station);
  const stored = matchingStoredGasStation(
    location,
    Array.isArray(storedStations) ? storedStations : gasStations
  );
  return {
    ...(stored || {}),
    id: station.shopId,
    shopId: station.shopId,
    isDigitalFuel: true,
    name: station.name,
    Brand: stored?.Brand || digitalFuelBrandFromName(station.name),
    Address: digitalFuelStreetAddress(station.address),
    fullAddress: station.address,
    location: location
      ? `${location.latitude},${location.longitude}`
      : undefined,
    status: station.status,
    distance: station.distance,
    productGroups: station.productGroups,
    matchedStationId: stored?.id ?? null,
  };
};

export const nearestRentalGasStation = (
  carPosition: Location | null | undefined,
  stations: Array<any> | null | undefined
): any | null => {
  if (!stations || stations.length === 0) return null;
  const allLocated = stations.every((station) => !!station?.location);
  if (carPosition && allLocated) {
    return nearestGasStation(carPosition, stations) || stations[0];
  }
  return stations[0];
};

export const getFuelSessionStatus = (
  rentalData: any
): FuelSessionStatus | null => {
  const status = rentalData?.fuelSessionStatus;
  return typeof status === 'string'
    ? (status.toLowerCase() as FuelSessionStatus)
    : null;
};

export const isFuelSessionAcquired = (
  status: FuelSessionStatus | null | undefined
): boolean => status === 'acquired' || status === 'completed';
